import api from "./api";
import { parsePaginatedList } from "../utils/pagination";

export interface InventoryItem {
  id: string;
  productId: string;
  branchId: string;
  quantity: number;
  reservedQuantity: number;
  availableQuantity: number;
  reorderLevel: number;
  lastRestockedAt?: string;
  createdAt: string;
  updatedAt: string;
  product: {
    id: string;
    name: string;
    sku: string;
    productType: "GOODS" | "PREPARED";
  };
  branch: {
    id: string;
    name: string;
  };
}

export interface CreateInventoryData {
  productId: string;
  branchId: string;
  quantity: number;
  reservedQuantity?: number;
  availableQuantity?: number;
  reorderLevel?: number;
  lastRestockedAt?: string;
}

export interface UpdateInventoryData {
  productId?: string;
  branchId?: string;
  quantity?: number;
  reservedQuantity?: number;
  availableQuantity?: number;
  reorderLevel?: number;
  lastRestockedAt?: string;
}

export interface AdjustInventoryData {
  adjustmentType: "add" | "remove" | "wastage";
  quantity: number;
  reason: string;
  batchNumber?: string;
  expiryDate?: string;
  supplier?: string;
  costPrice?: number;
}

const inventoryAPI = {
  // Get all inventory with pagination
  getInventory: async (params?: {
    pageSize?: number;
    pageNo?: number;
    productId?: string;
    branchId?: string;
    search?: string;
    lowStock?: boolean;
    productType?: string;
    sortBy?: string;
    sortOrder?: "ASC" | "DESC";
  }): Promise<{ data: InventoryItem[]; total: number }> => {
    const response = await api.get("/inventory", { params });
    const { data, total } = parsePaginatedList<InventoryItem>(response.data.data);
    return { data, total };
  },

  // Get inventory by ID
  getInventoryById: async (id: string): Promise<InventoryItem> => {
    const response = await api.get(`/inventory/${id}`);
    return response.data.data;
  },

  // Get inventory by product
  getInventoryByProduct: async (
    productId: string
  ): Promise<InventoryItem[]> => {
    const response = await api.get(`/inventory/product/${productId}`);
    return response.data.data;
  },

  // Get inventory by branch
  getInventoryByBranch: async (branchId: string): Promise<InventoryItem[]> => {
    const response = await api.get(`/inventory/branch/${branchId}`);
    return response.data.data;
  },

  // Create new inventory
  createInventory: async (
    data: CreateInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.post("/inventory", data);
    return response.data.data;
  },

  // Update inventory
  updateInventory: async (
    id: string,
    data: UpdateInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.put(`/inventory/${id}`, data);
    return response.data.data;
  },

  // Adjust inventory (add/remove stock)
  adjustInventory: async (
    id: string,
    data: AdjustInventoryData
  ): Promise<InventoryItem> => {
    const response = await api.post(`/inventory/${id}/adjust`, data);
    return response.data.data;
  },

  // Get low stock items
  getLowStockItems: async (): Promise<InventoryItem[]> => {
    const response = await api.get("/inventory/low-stock");
    return response.data.data;
  },

  // Get inventory report
  getInventoryReport: async (): Promise<any> => {
    const response = await api.get("/inventory/report");
    return response.data.data;
  },

  // Delete inventory
  deleteInventory: async (id: string): Promise<void> => {
    await api.delete(`/inventory/${id}`);
  },
};

export default inventoryAPI;
